<template>
  <div class="code-form">
    <p class="code-form__subtitle body-m">{{ subtitle }}</p>

    <form class="code-form__form" @submit.prevent="$emit('submit', pin)">
      <p class="title-xs bold">{{ heading }}</p>

      <div class="code-form__code" @paste="onPaste">
        <input
          v-for="(digit, i) in digits"
          :key="i"
          ref="inputs"
          :value="digit"
          type="text"
          inputmode="numeric"
          autocomplete="one-time-code"
          maxlength="1"
          class="code-form__digit"
          :aria-label="$t('components.codeInput.digitLabel', { n: i + 1 })"
          @input="onInput(i, $event)"
          @keydown="onKeydown(i, $event)"
          @focus="$event.target.select()"
        >
      </div>

      <p class="code-form__hint body-xs">{{ hint }}</p>

      <slot name="messages" />

      <div class="code-form__actions">
        <button
          type="submit"
          class="code-form__submit"
          :disabled="!isComplete || loading"
        >
          {{ submitLabel }}
        </button>
        <div class="code-form__secondary">
          <slot name="secondary" />
        </div>
      </div>
    </form>
  </div>
</template>

<script>
export default {
  name: 'CodeInputForm',
  props: {
    subtitle: {
      type: String,
      default: ''
    },
    heading: {
      type: String,
      default: ''
    },
    hint: {
      type: String,
      default: ''
    },
    submitLabel: {
      type: String,
      default: ''
    },
    length: {
      type: Number,
      default: 6
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      digits: Array(this.length).fill('')
    }
  },
  computed: {
    pin () {
      return this.digits.join('')
    },
    isComplete () {
      return this.digits.every(d => d !== '')
    }
  },
  mounted () {
    this.focusInput(0)
  },
  methods: {
    // exposed to parents via ref: wipes the code and puts the caret back at the
    // first digit (used when a fresh code is requested)
    clear () {
      this.digits = Array(this.length).fill('')
      // onInput writes straight to the DOM, so the elements can be out of step
      // with `digits`; reset them explicitly instead of trusting the re-render
      const els = this.$refs.inputs || []
      els.forEach(el => { el.value = '' })
      this.focusInput(0)
      this.$emit('input', this.pin)
    },
    focusInput (index) {
      const el = this.$refs.inputs && this.$refs.inputs[index]
      if (el) {
        el.focus()
        el.select()
      }
    },
    onInput (index, event) {
      // keep only the last typed digit, ignore anything non-numeric
      const digit = event.target.value.replace(/\D/g, '').slice(-1)
      this.$set(this.digits, index, digit)
      // re-sync the DOM in case stripped characters left it out of step
      event.target.value = digit
      if (digit && index < this.length - 1) {
        this.focusInput(index + 1)
      }
      this.$emit('input', this.pin)
    },
    onKeydown (index, event) {
      if (event.key === 'Backspace') {
        event.preventDefault()
        if (this.digits[index]) {
          this.$set(this.digits, index, '')
        } else if (index > 0) {
          this.$set(this.digits, index - 1, '')
          this.focusInput(index - 1)
        }
      } else if (event.key === 'ArrowLeft' && index > 0) {
        this.focusInput(index - 1)
      } else if (event.key === 'ArrowRight' && index < this.length - 1) {
        this.focusInput(index + 1)
      }
    },
    onPaste (event) {
      event.preventDefault()
      const text = (event.clipboardData && event.clipboardData.getData('text')) || ''
      const nums = text.replace(/\D/g, '').slice(0, this.length).split('')
      if (!nums.length) return
      this.digits = Array.from({ length: this.length }, (_, i) => nums[i] || '')
      this.focusInput(Math.min(nums.length, this.length - 1))
      this.$emit('input', this.pin)
    }
  }
}
</script>

<style lang="scss" scoped>
.code-form {
  width: 100%;
  text-align: center;

  &__subtitle {
    margin: 0 auto $spacer*1.5;
    max-width: 360px;
  }

  &__form {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }

  &__code {
    display: flex;
    justify-content: center;
    gap: 8px;
    width: 100%;
  }

  &__digit {
    flex: 1 1 0;
    min-width: 0;
    max-width: 64px;
    height: 64px;
    padding: 0;
    // neutral/warmgrey/300 — design border, no matching global token
    border: 1px solid #d5d3d1;
    border-radius: 8px;
    background-color: $nl-surface;
    font-family: 'Source Sans Pro';
    font-size: 2rem;
    font-weight: 600;
    line-height: 44.8px;
    text-align: center;
    // neutral/warmgrey/950 — design text color
    color: #0c0a09;
    outline: none;
    transition: border-color 0.15s ease;

    &:focus {
      border-color: $brand-500;
    }
  }

  &__hint {
    margin: $spacer*0.75 0 0;
    // Grey/Grey 700 — design hint color
    color: #626262;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $spacer;
    width: 100%;
    margin-top: $spacer*1.5;
  }

  &__submit {
    display: block;
    width: 100%;
    padding: 15px 24px;
    border: 1px solid $brand-500;
    border-radius: 100px;
    background-color: $brand-500;
    font-family: 'Source Sans Pro';
    font-size: 1.25rem;
    font-weight: 600;
    line-height: 26px;
    color: $nl-surface;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    &:hover,
    &:active {
      background-color: $brand-600;
      border-color: $brand-600;
    }

    &:disabled {
      opacity: 0.7;
      cursor: default;
    }
  }

  // Slotted secondary action (resend). Scoped CSS can't reach slot content
  // directly, so style the child button/link via ::v-deep. Matches the
  // body-m semibold link look for both <button> (2FA) and <router-link> (recover).
  &__secondary ::v-deep {
    button,
    a {
      display: inline-block;
      padding: 8px;
      border: 0;
      background: transparent;
      font-family: 'Source Sans Pro';
      font-size: 1.25rem;
      font-weight: 600;
      line-height: 26px;
      color: #222222;
      text-decoration: none;
      cursor: pointer;

      &:not(:disabled):hover {
        text-decoration: underline;
      }

      // only <button> can be disabled — the <router-link> variant is unaffected
      &:disabled {
        color: #626262;
        cursor: default;
      }
    }
  }
}
</style>
